// src/components/admin/AnalyticsDashboardTab.jsx
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useSalonAuth } from '../../contexts/SalonAuthContext';
import { getSalonConfig } from '../../api/salonConfigService';
import '../../styles/reports.css';

const AnalyticsDashboardTab = () => {
  // Analytics Dashboard State
  const [analyticsData, setAnalyticsData] = useState({
    revenueByStaffs: [],
    revenueByServices: [],
    revenueByPeriod: [],
    tierSpending: [],
    tierVisits: [],
    visitsByStaff: [],
    visitsByService: [],
    staffs: [],
    services: []
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [salonConfig, setSalonConfig] = useState(null);
  const [analysisPeriod, setAnalysisPeriod] = useState('This Month');
  const [analyticsFilters, setAnalyticsFilters] = useState({
    revenueByStaffs: { serviceType: 'all' },
    revenueByServices: { staff: 'all' },
    revenueByPeriod: { period: 'By Month' },
    tierSpending: { tier: 'all' },
    tierVisits: { tier: 'all' },
    visitsByStaff: { serviceType: 'all' },
    visitsByService: { staff: 'all' }
  });

  // Raw data storage for filtering
  const [rawAnalyticsData, setRawAnalyticsData] = useState({
    customers: [],
    transactions: [],
    staffs: [],
    services: []
  });

  // Campaign Performance State
  const [campaignPerformance, setCampaignPerformance] = useState([]);
  const [campaignPerfLoading, setCampaignPerfLoading] = useState(false);
  const [campaignPerfFilters, setCampaignPerfFilters] = useState({
    channel: 'all',
    period: 'Last 12 Months'
  });
  const campaignPeriodOptions = ['This Month', 'Last 4 Weeks', 'Last 12 Months', 'Last 3 Years'];

  // Get current user from auth context
  const { currentUser } = useSalonAuth();

  // Analysis period options
  const analysisPeriodOptions = [
    'This Week',
    'This Month', 
    'This Year',
    'Last 4 Weeks',
    'Last 12 Months',
    'Last 3 Years'
  ];

  // Load salon configuration
  const loadSalonConfig = async () => {
    if (!currentUser?.salonId) return;
    
    try {
      const config = await getSalonConfig(currentUser.salonId);
      setSalonConfig(config);
    } catch (error) {
      console.error('Error loading salon config:', error);
    }
  };

  // Helper function to safely convert Firestore timestamp to Date
  const parseFirestoreDate = (dateValue) => {
    if (!dateValue) return null;
    
    // If it's already a Date object
    if (dateValue instanceof Date) return dateValue;
    
    // If it's a Firestore timestamp with seconds
    if (dateValue.seconds) {
      return new Date(dateValue.seconds * 1000);
    }
    
    // If it's an ISO string
    if (typeof dateValue === 'string') {
      return new Date(dateValue);
    }
    
    // If it's a timestamp number
    if (typeof dateValue === 'number') {
      // Check if it's in seconds (Unix timestamp) or milliseconds
      if (dateValue < 10000000000) { // Assume seconds if less than this
        return new Date(dateValue * 1000);
      } else {
        return new Date(dateValue);
      }
    }
    
    return null;
  };

  // Fetch staffs data
  const fetchStaffsData = async () => {
    if (!currentUser?.salonId) {
      throw new Error('No salon ID found');
    }
    
    try {
      console.log('Fetching staffs for salon:', currentUser.salonId);
      const staffsRef = collection(db, 'staff');
      const staffsQuery = query(staffsRef, where('salonId', '==', currentUser.salonId));
      const staffsSnapshot = await getDocs(staffsQuery);
      
      const staffs = [];
      staffsSnapshot.forEach(doc => {
        const staffData = doc.data();
        staffs.push({
          id: doc.id,
          ...staffData
        });
      });
      
      console.log('Found staffs:', staffs.length);
      return staffs;
    } catch (error) {
      console.error('Error fetching staffs:', error);
      return [];
    }
  };

  // Fetch services data
  const fetchServicesData = async () => {
    if (!currentUser?.salonId) {
      throw new Error('No salon ID found');
    }
    
    try {
      console.log('Fetching services for salon:', currentUser.salonId);
      const servicesRef = collection(db, 'services');
      const servicesQuery = query(servicesRef, where('salonId', '==', currentUser.salonId));
      const servicesSnapshot = await getDocs(servicesQuery);
      
      const services = [];
      servicesSnapshot.forEach(doc => {
        const serviceData = doc.data();
        services.push({
          id: doc.id,
          ...serviceData
        });
      });
      
      console.log('Found services:', services.length);
      return services;
    } catch (error) {
      console.error('Error fetching services:', error);
      return [];
    }
  };

  // Fetch customers data from flat collection
  const fetchCustomersData = async () => {
    if (!currentUser?.salonId) {
      throw new Error('No salon ID found');
    }
    
    console.log('Fetching customers for salon:', currentUser.salonId);
    
    try {
      const customersRef = collection(db, 'customers');
      const customersQuery = query(customersRef, where('salonId', '==', currentUser.salonId));
      const customersSnapshot = await getDocs(customersQuery);
      
      const customers = [];
      customersSnapshot.forEach(customerDoc => {
        const customerData = customerDoc.data();
        customers.push({
          id: customerDoc.id,
          ...customerData,
          totalSpent: customerData.metrics?.totalSpent || 0,
          pointsBalance: customerData.metrics?.pointsBalance || 0,
          pointsEarned: customerData.metrics?.pointsEarned || 0,
          pointsRedeemed: customerData.metrics?.pointsRedeemed || 0,
          lastTransaction: customerData.metrics?.lastTransaction
        });
      });
      
      console.log('Found customers:', customers.length);
      return customers;
    } catch (error) {
      console.error('Error fetching customers:', error);
      return [];
    }
  };

  // Fetch transactions from flat collection
  const fetchTransactionsData = async () => {
    if (!currentUser?.salonId) {
      throw new Error('No salon ID found');
    }
    
    try {
      console.log('Fetching transactions for salon:', currentUser.salonId);
      const transactionsRef = collection(db, 'transactions');
      const transactionsQuery = query(
        transactionsRef, 
        where('salonId', '==', currentUser.salonId),
        where('paymentStatus', '==', 'Completed')
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);
      
      const transactions = [];
      transactionsSnapshot.forEach(doc => {
        const transactionData = doc.data();
        transactions.push({
          id: doc.id,
          ...transactionData,
          // Convert amount from string to number for calculations
          amount: parseFloat(transactionData.amount || transactionData.totalAmount || 0),
          totalAmount: parseFloat(transactionData.totalAmount || transactionData.amount || 0),
          price: parseFloat(transactionData.price || transactionData.amount || 0)
        });
      });
      
      console.log('Found completed transactions:', transactions.length);
      return transactions;
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return [];
    }
  };

  // Get date range for analysis period
  const getAnalysisPeriodRange = (period) => {
    const now = new Date();
    let startDate = new Date();
    let endDate = new Date(now);
    
    switch (period) {
      case 'This Week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - now.getDay());
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
        
      case 'This Month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
        
      case 'This Year':
        startDate = new Date(now.getFullYear(), 0, 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now.getFullYear(), 11, 31);
        endDate.setHours(23, 59, 59, 999);
        break;
        
      case 'Last 4 Weeks':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 28);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
        
      case 'Last 12 Months':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 12);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
        
      case 'Last 3 Years':
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 3);
        startDate.setMonth(0);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
        
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        endDate.setHours(23, 59, 59, 999);
    }
    
    console.log(`Date range for ${period}:`, { startDate, endDate });
    return { startDate, endDate };
  };

  // Filter transactions by analysis period with proper date handling
  const filterTransactionsByPeriod = (transactions, period) => {
    const { startDate, endDate } = getAnalysisPeriodRange(period);
    
    const filteredTransactions = transactions.filter(transaction => {
      // Try multiple date fields that might exist
      const paidDate = parseFirestoreDate(transaction.paidAt) || 
                      parseFirestoreDate(transaction.createdAt) ||
                      parseFirestoreDate(transaction.dateTime);
      
      if (!paidDate) {
        console.log('Transaction without valid date:', transaction.id, transaction);
        return false;
      }
      
      const isInRange = paidDate >= startDate && paidDate <= endDate;
      if (isInRange) {
        console.log('Transaction in range:', transaction.id, paidDate, { startDate, endDate });
      }
      
      return isInRange;
    });
    
    console.log(`Filtered ${transactions.length} transactions to ${filteredTransactions.length} for period ${period}`);
    return filteredTransactions;
  };

  // Calculate revenue by staffs with service filter
  const calculateRevenueByStaffs = (transactions, services, serviceFilter = 'all') => {
    let filteredTransactions = transactions;

    // Apply service filter if not 'all'
    if (serviceFilter !== 'all') {
      filteredTransactions = transactions.filter(transaction =>
        transaction.serviceName === serviceFilter
      );
    }

    const staffRevenue = {};

    filteredTransactions.forEach(transaction => {
      // Support both 'stylistName' (seeded field) and legacy 'staffName'
      const staff = transaction.stylistName || transaction.staffName || 'Unknown Staff';
      const amount = parseFloat(transaction.price || transaction.amount || transaction.totalAmount || 0);
      
      if (!staffRevenue[staff]) {
        staffRevenue[staff] = {
          name: staff,
          revenue: 0,
          appointments: 0
        };
      }
      
      staffRevenue[staff].revenue += amount;
      staffRevenue[staff].appointments += 1;
    });
    
    return Object.values(staffRevenue).sort((a, b) => b.revenue - a.revenue);
  };

  // Calculate revenue by services with staff filter
  const calculateRevenueByServices = (transactions, staffs, staffFilter = 'all') => {
    // Only count service transactions — exclude product purchases
    let filteredTransactions = transactions.filter(t => t.type === 'service');

    // Apply staff filter if not 'all'
    if (staffFilter !== 'all') {
      filteredTransactions = filteredTransactions.filter(transaction =>
        (transaction.stylistName || transaction.staffName) === staffFilter
      );
    }

    const serviceRevenue = {};
    
    filteredTransactions.forEach(transaction => {
      const service = transaction.serviceName || 'Unknown Service';
      const amount = parseFloat(transaction.price || transaction.amount || transaction.totalAmount || 0);
      
      if (!serviceRevenue[service]) {
        serviceRevenue[service] = {
          name: service,
          revenue: 0,
          count: 0
        };
      }
      
      serviceRevenue[service].revenue += amount;
      serviceRevenue[service].count += 1;
    });
    
    return Object.values(serviceRevenue).sort((a, b) => b.revenue - a.revenue);
  };

  // Calculate revenue by period
  const calculateRevenueByPeriod = (transactions, period) => {
    const revenueByPeriod = {};
    
    transactions.forEach(transaction => {
      const paidDate = parseFirestoreDate(transaction.paidAt) || 
                      parseFirestoreDate(transaction.createdAt) ||
                      parseFirestoreDate(transaction.dateTime);
      
      if (!paidDate) return;
      
      let periodKey;
      switch (period) {
        case 'By Day':
          periodKey = paidDate.toISOString().split('T')[0];
          break;
        case 'By Week':
          const weekStart = new Date(paidDate);
          weekStart.setDate(paidDate.getDate() - paidDate.getDay());
          periodKey = weekStart.toISOString().split('T')[0];
          break;
        case 'By Month':
          periodKey = `${paidDate.getFullYear()}-${(paidDate.getMonth() + 1).toString().padStart(2, '0')}`;
          break;
        case 'By Quarter':
          const quarter = Math.floor(paidDate.getMonth() / 3) + 1;
          periodKey = `${paidDate.getFullYear()}-Q${quarter}`;
          break;
        case 'By Year':
          periodKey = paidDate.getFullYear().toString();
          break;
        default:
          periodKey = paidDate.toISOString().split('T')[0];
      }
      
      if (!revenueByPeriod[periodKey]) {
        revenueByPeriod[periodKey] = {
          period: periodKey,
          revenue: 0,
          transactions: 0
        };
      }
      
      revenueByPeriod[periodKey].revenue += parseFloat(transaction.price || transaction.amount || transaction.totalAmount || 0);
      revenueByPeriod[periodKey].transactions += 1;
    });
    
    return Object.values(revenueByPeriod).sort((a, b) => a.period.localeCompare(b.period));
  };

  // Calculate tier spending with tier filter
  const calculateTierSpendingByPeriod = (customers, filteredTransactions, tierFilter = 'all') => {
    const tierSpending = {
      'Bronze': { tier: 'Bronze', spending: 0, customers: 0 },
      'Silver': { tier: 'Silver', spending: 0, customers: 0 },
      'Gold': { tier: 'Gold', spending: 0, customers: 0 },
      'Platinum': { tier: 'Platinum', spending: 0, customers: 0 }
    };
    
    // Filter customers by tier if not 'all'
    let filteredCustomers = customers;
    if (tierFilter !== 'all') {
      filteredCustomers = customers.filter(customer => customer.loyaltyTier === tierFilter);
    }
    
    filteredCustomers.forEach(customer => {
      const tier = customer.loyaltyTier || 'Bronze';
      if (tierSpending[tier]) {
        tierSpending[tier].customers += 1;
        
        // Calculate spending from filtered transactions
        const customerTransactions = filteredTransactions.filter(t => t.customerId === customer.id);
        const customerSpending = customerTransactions.reduce((sum, t) => 
          sum + parseFloat(t.price || t.amount || t.totalAmount || 0), 0);
        tierSpending[tier].spending += customerSpending;
      }
    });
    
    // If filtering by tier, only return that tier's data
    if (tierFilter !== 'all') {
      return tierSpending[tierFilter] ? [tierSpending[tierFilter]] : [];
    }
    
    return Object.values(tierSpending);
  };

  // Calculate tier visits with tier filter
  const calculateTierVisitsByPeriod = (customers, filteredTransactions, tierFilter = 'all') => {
    const tierVisits = {
      'Bronze': { tier: 'Bronze', visits: 0 },
      'Silver': { tier: 'Silver', visits: 0 },
      'Gold': { tier: 'Gold', visits: 0 },
      'Platinum': { tier: 'Platinum', visits: 0 }
    };
    
    // Filter transactions by customer tier
    const transactionsToProcess = tierFilter === 'all' 
      ? filteredTransactions
      : filteredTransactions.filter(transaction => {
          const customer = customers.find(c => c.id === transaction.customerId);
          return customer?.loyaltyTier === tierFilter;
        });
    
    transactionsToProcess.forEach(transaction => {
      const customer = customers.find(c => c.id === transaction.customerId);
      const tier = customer?.loyaltyTier || 'Bronze';
      
      if (tierVisits[tier]) {
        tierVisits[tier].visits += 1;
      }
    });
    
    // If filtering by tier, only return that tier's data
    if (tierFilter !== 'all') {
      return tierVisits[tierFilter] ? [tierVisits[tierFilter]] : [];
    }
    
    return Object.values(tierVisits);
  };

  // Calculate total visits by staff (counting unique createdAt dates) with service filter
  const calculateVisitsByStaff = (transactions, serviceFilter = 'all') => {
    let filteredTransactions = transactions;

    // Apply service filter if not 'all'
    if (serviceFilter !== 'all') {
      filteredTransactions = transactions.filter(transaction =>
        transaction.serviceName === serviceFilter
      );
    }

    const staffVisits = {};

    filteredTransactions.forEach(transaction => {
      // Support both 'stylistName' (seeded field) and legacy 'staffName'
      const staff = transaction.stylistName || transaction.staffName || 'Unknown Staff';

      // Get the date portion of createdAt
      const createdDate = parseFirestoreDate(transaction.createdAt);
      const dateKey = createdDate ? createdDate.toISOString().split('T')[0] : null;

      if (!dateKey) return; // Skip if no valid date

      if (!staffVisits[staff]) {
        staffVisits[staff] = {
          name: staff,
          visits: 0,
          uniqueDates: new Set()
        };
      }

      // Only count if this date hasn't been counted for this staff yet
      staffVisits[staff].uniqueDates.add(dateKey);
    });

    // Convert Set size to visit count
    const result = Object.values(staffVisits).map(staff => ({
      name: staff.name,
      visits: staff.uniqueDates.size
    }));

    return result.sort((a, b) => b.visits - a.visits);
  };

  // Calculate total visits by service (counting unique createdAt dates) with staff filter
  const calculateVisitsByService = (transactions, staffFilter = 'all') => {
    // Only count service transactions — exclude product purchases
    let filteredTransactions = transactions.filter(t => t.type === 'service');

    // Apply staff filter if not 'all'
    if (staffFilter !== 'all') {
      filteredTransactions = filteredTransactions.filter(transaction =>
        (transaction.stylistName || transaction.staffName) === staffFilter
      );
    }

    const serviceVisits = {};

    filteredTransactions.forEach(transaction => {
      const service = transaction.serviceName || 'Unknown Service';

      // Get the date portion of createdAt
      const createdDate = parseFirestoreDate(transaction.createdAt);
      const dateKey = createdDate ? createdDate.toISOString().split('T')[0] : null;

      if (!dateKey) return; // Skip if no valid date

      if (!serviceVisits[service]) {
        serviceVisits[service] = {
          name: service,
          visits: 0,
          uniqueDates: new Set()
        };
      }

      // Only count if this date hasn't been counted for this service yet
      serviceVisits[service].uniqueDates.add(dateKey);
    });

    // Convert Set size to visit count
    const result = Object.values(serviceVisits).map(service => ({
      name: service.name,
      visits: service.uniqueDates.size
    }));

    return result.sort((a, b) => b.visits - a.visits);
  };

  // Load Campaign Performance Data
  const loadCampaignPerformance = async (filters = campaignPerfFilters, allTransactions = rawAnalyticsData.transactions) => {
    if (!currentUser?.salonId) return;
    setCampaignPerfLoading(true);
    try {
      const { startDate, endDate } = getAnalysisPeriodRange(filters.period);

      // Fetch all three channel collections in parallel
      const channelQueries = [
        getDocs(query(collection(db, 'mail'), where('salonId', '==', currentUser.salonId), where('metadata.campaignType', '==', 'actual_blast'))),
        getDocs(query(collection(db, 'pushNotifications'), where('salonId', '==', currentUser.salonId), where('metadata.campaignType', '==', 'actual_blast'))),
        getDocs(query(collection(db, 'whatsappMessages'), where('salonId', '==', currentUser.salonId), where('metadata.campaignType', '==', 'actual_blast')))
      ];
      const [emailSnap, pushSnap, waSnap] = await Promise.all(channelQueries);

      const channelLabels = { email: 'Email', push: 'Push', whatsapp: 'WhatsApp' };
      const rawCampaigns = [];

      const processDocs = (snap, channelKey) => {
        snap.forEach(docSnap => {
          const d = docSnap.data();
          const sentAt = d.metadata?.timestamp ? new Date(d.metadata.timestamp) :
            (d.sentAt?.seconds ? new Date(d.sentAt.seconds * 1000) :
            (d.metadata?.scheduledDate && d.metadata?.scheduledTime
              ? new Date(`${d.metadata.scheduledDate}T${d.metadata.scheduledTime}`)
              : null));
          if (!sentAt) return;
          if (sentAt < startDate || sentAt > endDate) return;
          if (filters.channel !== 'all' && filters.channel !== channelKey) return;

          // Extract recipient emails:
          // - email campaigns: `to` field (array of strings or {email} objects)
          // - push/whatsapp campaigns: `targetRecipients` array of {email, ...}
          let recipientEmails = [];
          if (d.to) {
            const toArr = Array.isArray(d.to) ? d.to : [d.to];
            recipientEmails = toArr.map(r => typeof r === 'object' ? (r.email || '') : String(r)).filter(Boolean);
          } else if (d.targetRecipients) {
            recipientEmails = d.targetRecipients.map(r => r.email || '').filter(Boolean);
          }

          rawCampaigns.push({
            id: docSnap.id,
            batchId: d.metadata?.campaignBatchId || docSnap.id,
            channel: channelKey,
            channelLabel: channelLabels[channelKey],
            title: d.subject || d.title || 'Campaign',
            sentAt,
            recipientEmails,
            recipientCount: recipientEmails.length,
            delivered: d.analytics?.delivered ?? d.successCount ?? d.delivery?.successCount ?? recipientEmails.length,
            opens: d.analytics?.openedBy?.length ?? d.analytics?.opens ?? 0,
            clicks: d.analytics?.clickedBy?.length ?? d.analytics?.clicks ?? 0,
          });
        });
      };

      processDocs(emailSnap, 'email');
      processDocs(pushSnap, 'push');
      processDocs(waSnap, 'whatsapp');

      // Consolidate email docs by campaignBatchId (multiple docs per personalized blast)
      const batchMap = {};
      rawCampaigns.forEach(c => {
        const key = c.batchId;
        if (!batchMap[key]) {
          batchMap[key] = { ...c, recipientEmails: [...c.recipientEmails] };
        } else {
          batchMap[key].recipientCount += c.recipientCount;
          batchMap[key].recipientEmails = [...new Set([...batchMap[key].recipientEmails, ...c.recipientEmails])];
          batchMap[key].delivered += c.delivered;
          batchMap[key].opens += c.opens;
          batchMap[key].clicks += c.clicks;
        }
      });

      const campaigns = Object.values(batchMap);

      // Build a lookup: lowercase email → customerId from the transactions list so we can match push/WA campaigns
      // Also build reverse lookup: customerId → email from rawAnalyticsData.customers
      const { customers: allCustomers } = rawAnalyticsData;
      const emailToCustomerId = {};
      allCustomers.forEach(c => {
        if (c.email) emailToCustomerId[c.email.toLowerCase()] = c.id;
      });

      // Revenue attribution: transactions from campaign recipients within 30 days after send
      const txs = allTransactions.length ? allTransactions : (rawAnalyticsData.transactions || []);

      // Build transaction lookup by customerId for speed
      const txByCustomer = {};
      txs.forEach(tx => {
        if (!tx.customerId) return;
        if (!txByCustomer[tx.customerId]) txByCustomer[tx.customerId] = [];
        txByCustomer[tx.customerId].push(tx);
      });

      campaigns.forEach(campaign => {
        const attributionEnd = new Date(campaign.sentAt.getTime() + 30 * 24 * 60 * 60 * 1000);

        // Resolve recipient emails → customerIds
        const recipientCustomerIds = new Set();
        campaign.recipientEmails.forEach(email => {
          const cid = emailToCustomerId[email.toLowerCase()];
          if (cid) recipientCustomerIds.add(cid);
        });

        // Also keep a raw email set for direct email-on-transaction matching
        const recipientEmailSet = new Set(campaign.recipientEmails.map(e => e.toLowerCase()));

        let attributedRevenue = 0;
        let attributedBookings = 0;

        recipientCustomerIds.forEach(cid => {
          const customerTxs = txByCustomer[cid] || [];
          customerTxs.forEach(tx => {
            const txDate = parseFirestoreDate(tx.paidAt) || parseFirestoreDate(tx.createdAt) || parseFirestoreDate(tx.dateTime);
            if (!txDate) return;
            if (txDate < campaign.sentAt || txDate > attributionEnd) return;
            attributedRevenue += parseFloat(tx.price || tx.amount || tx.totalAmount || 0);
            attributedBookings += 1;
          });
        });

        // Fallback: match by customerEmail field directly on transaction (for any gaps)
        if (attributedRevenue === 0 && recipientEmailSet.size > 0) {
          txs.forEach(tx => {
            if (!tx.customerEmail) return;
            if (!recipientEmailSet.has(tx.customerEmail.toLowerCase())) return;
            const txDate = parseFirestoreDate(tx.paidAt) || parseFirestoreDate(tx.createdAt) || parseFirestoreDate(tx.dateTime);
            if (!txDate) return;
            if (txDate < campaign.sentAt || txDate > attributionEnd) return;
            attributedRevenue += parseFloat(tx.price || tx.amount || tx.totalAmount || 0);
            attributedBookings += 1;
          });
        }

        campaign.attributedRevenue = attributedRevenue;
        campaign.attributedBookings = attributedBookings;
      });

      campaigns.sort((a, b) => b.sentAt - a.sentAt);
      setCampaignPerformance(campaigns);
    } catch (err) {
      console.error('Error loading campaign performance:', err);
    } finally {
      setCampaignPerfLoading(false);
    }
  };

  // Load Analytics Data and store raw data for filtering
  const loadAnalyticsData = async () => {
    if (!currentUser?.salonId) return;
    
    setAnalyticsLoading(true);
    try {
      console.log('Loading analytics data for salon:', currentUser.salonId);
      
      // Fetch all data from flat collections
      const [customers, allTransactions, staffs, services] = await Promise.all([
        fetchCustomersData(),
        fetchTransactionsData(),
        fetchStaffsData(),
        fetchServicesData()
      ]);
      
      console.log('Total customers:', customers.length);
      console.log('Total transactions:', allTransactions.length);
      console.log('Total staffs:', staffs.length);
      console.log('Total services:', services.length);
      
      // Store raw data for filtering
      setRawAnalyticsData({
        customers,
        transactions: allTransactions,
        staffs,
        services
      });
      
      if (allTransactions.length === 0) {
        console.log('No completed transactions found');
        setAnalyticsData({
          revenueByStaffs: [],
          revenueByServices: [],
          revenueByPeriod: [],
          tierSpending: [],
          tierVisits: [],
          visitsByStaff: [],
          visitsByService: [],
          staffs: [],
          services: []
        });
        return;
      }
      
      // Enrich transactions with customer data
      const enrichedTransactions = allTransactions.map(transaction => {
        const customer = customers.find(c => c.id === transaction.customerId);
        return {
          ...transaction,
          customer: customer || {},
          customerTier: customer?.loyaltyTier || 'Bronze'
        };
      });
      
      // Filter transactions by analysis period for period-based charts
      const filteredTransactions = filterTransactionsByPeriod(enrichedTransactions, analysisPeriod);
      console.log(`Filtered transactions for ${analysisPeriod}:`, filteredTransactions.length);
      
      // Calculate analytics with current filters
      const revenueByStaffs = calculateRevenueByStaffs(
        filteredTransactions, 
        services, 
        analyticsFilters.revenueByStaffs.serviceType
      );
      
      const revenueByServices = calculateRevenueByServices(
        filteredTransactions, 
        staffs, 
        analyticsFilters.revenueByServices.staff
      );
      
      const revenueByPeriod = calculateRevenueByPeriod(enrichedTransactions, analyticsFilters.revenueByPeriod.period);
      
      const tierSpending = calculateTierSpendingByPeriod(
        customers, 
        filteredTransactions, 
        analyticsFilters.tierSpending.tier
      );
      
      const tierVisits = calculateTierVisitsByPeriod(
        customers,
        filteredTransactions,
        analyticsFilters.tierVisits.tier
      );

      const visitsByStaff = calculateVisitsByStaff(
        filteredTransactions,
        analyticsFilters.visitsByStaff.serviceType
      );

      const visitsByService = calculateVisitsByService(
        filteredTransactions,
        analyticsFilters.visitsByService.staff
      );

      // Extract unique staffs and services for filters from salon config (not transaction data)
      const uniqueStaffs = staffs.map(s => s.name).filter(Boolean);
      const uniqueServices = services.map(s => s.name).filter(Boolean);

      console.log('Analytics calculated:', {
        revenueByStaffs: revenueByStaffs.length,
        revenueByServices: revenueByServices.length,
        revenueByPeriod: revenueByPeriod.length,
        tierSpending: tierSpending.length,
        tierVisits: tierVisits.length,
        visitsByStaff: visitsByStaff.length,
        visitsByService: visitsByService.length
      });

      setAnalyticsData({
        revenueByStaffs,
        revenueByServices,
        revenueByPeriod,
        tierSpending,
        tierVisits,
        visitsByStaff,
        visitsByService,
        staffs: uniqueStaffs,
        services: uniqueServices
      });
      
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Function to recalculate analytics with new filters
  const recalculateAnalyticsWithFilters = () => {
    const { customers, transactions: allTransactions } = rawAnalyticsData;
    
    if (!customers.length || !allTransactions.length) return;
    
    // Enrich transactions
    const enrichedTransactions = allTransactions.map(transaction => {
      const customer = customers.find(c => c.id === transaction.customerId);
      return {
        ...transaction,
        customer: customer || {},
        customerTier: customer?.loyaltyTier || 'Bronze'
      };
    });
    
    // Filter by analysis period
    const filteredTransactions = filterTransactionsByPeriod(enrichedTransactions, analysisPeriod);
    
    // Recalculate with current filters
    const revenueByStaffs = calculateRevenueByStaffs(
      filteredTransactions, 
      rawAnalyticsData.services, 
      analyticsFilters.revenueByStaffs.serviceType
    );
    
    const revenueByServices = calculateRevenueByServices(
      filteredTransactions, 
      rawAnalyticsData.staffs, 
      analyticsFilters.revenueByServices.staff
    );
    
    const tierSpending = calculateTierSpendingByPeriod(
      customers, 
      filteredTransactions, 
      analyticsFilters.tierSpending.tier
    );
    
    const tierVisits = calculateTierVisitsByPeriod(
      customers,
      filteredTransactions,
      analyticsFilters.tierVisits.tier
    );

    const visitsByStaff = calculateVisitsByStaff(
      filteredTransactions,
      analyticsFilters.visitsByStaff.serviceType
    );

    const visitsByService = calculateVisitsByService(
      filteredTransactions,
      analyticsFilters.visitsByService.staff
    );

    const revenueByPeriod = calculateRevenueByPeriod(enrichedTransactions, analyticsFilters.revenueByPeriod.period);

    // Update analytics data
    setAnalyticsData(prev => ({
      ...prev,
      revenueByStaffs,
      revenueByServices,
      tierSpending,
      tierVisits,
      visitsByStaff,
      visitsByService,
      revenueByPeriod
    }));
  };

  // Load analytics data when component mounts or when analysis period changes
  useEffect(() => {
    loadSalonConfig();
    loadAnalyticsData();
  }, [analysisPeriod]);

  // Recalculate when filters change (but not when first loading)
  useEffect(() => {
    if (rawAnalyticsData.transactions.length > 0) {
      recalculateAnalyticsWithFilters();
    }
  }, [analyticsFilters]);

  // Load campaign performance when campaign filters change or after transactions load
  useEffect(() => {
    if (rawAnalyticsData.transactions.length > 0) {
      loadCampaignPerformance(campaignPerfFilters, rawAnalyticsData.transactions);
    }
  }, [campaignPerfFilters, rawAnalyticsData.transactions]);

  // Bar Chart Component for Revenue by Period
  const LineChart = ({ data, title, filter, onFilterChange }) => {
    if (!data || data.length === 0) {
      return (
        <div className="analytics-chart-card line-chart-full-width">
          <div className="chart-header">
            <h3>{title}</h3>
            {filter && (
              <div className="chart-filter">
                <select
                  value={filter.value}
                  onChange={(e) => onFilterChange(e.target.value)}
                  className="filter-select"
                >
                  {filter.options.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="no-chart-data">No data available</div>
        </div>
      );
    }

    // Ensure we always have exactly 12 data points for consistent X-axis
    const normalizeDataTo12Points = (inputData) => {
      if (inputData.length >= 12) {
        // If we have 12 or more points, take the last 12
        return inputData.slice(-12);
      } else {
        // If we have less than 12, pad with empty periods at the beginning
        const paddedData = [];
        const pointsNeeded = 12 - inputData.length;

        for (let i = 0; i < pointsNeeded; i++) {
          paddedData.push({
            period: '',
            revenue: 0,
            transactions: 0
          });
        }

        return [...paddedData, ...inputData];
      }
    };

    const normalizedData = normalizeDataTo12Points(data);
    const maxRevenue = Math.max(...normalizedData.map(item => item.revenue));
    const minRevenue = 0; // Start bars from 0 for better visualization
    const revenueRange = maxRevenue || 1;

    return (
      <div className="analytics-chart-card line-chart-full-width">
        <div className="chart-header">
          <h3>{title}</h3>
          {filter && (
            <div className="chart-filter">
              <select
                value={filter.value}
                onChange={(e) => onFilterChange(e.target.value)}
                className="filter-select"
              >
                {filter.options.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="line-chart-container">
          <svg
            width="100%"
            height="300"
            viewBox="0 0 1600 300"
            className="line-chart-svg"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Chart Grid */}
            <defs>
              <pattern id="grid" width="40" height="30" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 30" fill="none" stroke="#f0f0f0" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />

            {/* Vertical Bars */}
            {normalizedData.map((point, index) => {
              const barWidth = 1500 / normalizedData.length * 0.7; // 70% of available space for each bar
              const x = 50 + (index * (1500 / normalizedData.length)) + (1500 / normalizedData.length - barWidth) / 2;
              const barHeight = (point.revenue / revenueRange) * 200;
              const y = 250 - barHeight;

              return (
                <g key={index}>
                  {/* Bar */}
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={barHeight}
                    fill="#0070c9"
                    className="chart-bar"
                  >
                    <title>{`${point.period}: $${point.revenue.toFixed(2)}`}</title>
                  </rect>

                  {/* X-axis labels */}
                  {point.period && (
                    <text
                      x={x + barWidth / 2}
                      y="280"
                      textAnchor="middle"
                      fontSize="11"
                      fill="#666"
                      className="chart-label"
                    >
                      {point.period.length > 10 ? point.period.substring(0, 8) + '...' : point.period}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Y-axis labels */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
              const y = 250 - (ratio * 200);
              const value = minRevenue + (ratio * revenueRange);
              return (
                <text
                  key={index}
                  x="40"
                  y={y + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="#666"
                >
                  ${value.toFixed(0)}
                </text>
              );
            })}
          </svg>
        </div>

        <div className="chart-summary">
          <div className="summary-item">
            <span className="summary-label">Total Revenue:</span>
            <span className="summary-value">${data.reduce((sum, item) => sum + item.revenue, 0).toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Average:</span>
            <span className="summary-value">${(data.reduce((sum, item) => sum + item.revenue, 0) / data.length).toFixed(2)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Peak:</span>
            <span className="summary-value">${maxRevenue.toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Enhanced Chart Component for Analytics
  const AnalyticsChart = ({ data, title, type, filter, onFilterChange, formatValue }) => {
    if (!data || data.length === 0) {
      return (
        <div className="analytics-chart-card">
          <div className="chart-header">
            <h3>{title}</h3>
            {filter && (
              <div className="chart-filter">
                <select
                  value={filter.value}
                  onChange={(e) => onFilterChange(filter.key, e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All {filter.label}</option>
                  {filter.options.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="no-chart-data">No data available</div>
        </div>
      );
    }

    const maxValue = Math.max(...data.map(item => {
      if (type === 'revenue-by-staffs') return item.revenue;
      if (type === 'revenue-by-services') return item.revenue;
      if (type === 'tier-spending') return item.spending;
      if (type === 'tier-visits') return item.visits;
      if (type === 'visits-by-staff') return item.visits;
      if (type === 'visits-by-service') return item.visits;
      return 0;
    }));

    return (
      <div className="analytics-chart-card">
        <div className="chart-header">
          <h3>{title}</h3>
          {filter && (
            <div className="chart-filter">
              <select 
                value={filter.value} 
                onChange={(e) => onFilterChange(filter.key, e.target.value)}
                className="filter-select"
              >
                <option value="all">All {filter.label}</option>
                {filter.options.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        <div className="analytics-chart-content">
          {data.map((item, index) => {
            let label, value, subtitle = '';
            
            if (type === 'revenue-by-staffs') {
              label = item.name;
              value = item.revenue;
            } else if (type === 'revenue-by-services') {
              label = item.name;
              value = item.revenue;
            } else if (type === 'tier-spending') {
              label = item.tier;
              value = item.spending;
              subtitle = `${item.customers} customers`;
            } else if (type === 'tier-visits') {
              label = item.tier;
              value = item.visits;
            } else if (type === 'visits-by-staff') {
              label = item.name;
              value = item.visits;
            } else if (type === 'visits-by-service') {
              label = item.name;
              value = item.visits;
            }

            const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
            const colors = ['#0070c9', '#f7931e', '#00d4aa', '#ff6b35', '#9b59b6', '#e74c3c'];
            
            return (
              <div key={index} className="analytics-chart-item">
                <div className="chart-item-info">
                  <div className="chart-label">{label}</div>
                  {subtitle && <div className="chart-subtitle">{subtitle}</div>}
                </div>
                <div className="chart-value">
                  {formatValue ? formatValue(value) : `${value.toFixed(2)}`}
                </div>
                <div className="chart-bar-container">
                  <div 
                    className="analytics-chart-bar" 
                    style={{ 
                      width: `${percentage}%`,
                      backgroundColor: colors[index % colors.length]
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Campaign Performance: KPI summary row
  const CampaignPerfSummary = ({ campaigns }) => {
    const totalRevenue = campaigns.reduce((s, c) => s + c.attributedRevenue, 0);
    const totalBookings = campaigns.reduce((s, c) => s + c.attributedBookings, 0);
    const totalDelivered = campaigns.reduce((s, c) => s + (c.delivered || 0), 0);
    const totalOpens = campaigns.reduce((s, c) => s + c.opens, 0);
    const totalClicks = campaigns.reduce((s, c) => s + c.clicks, 0);
    const openRate = totalDelivered > 0 ? ((totalOpens / totalDelivered) * 100).toFixed(1) : '0.0';
    const clickRate = totalDelivered > 0 ? ((totalClicks / totalDelivered) * 100).toFixed(1) : '0.0';

    const kpis = [
      { label: 'Attributed Revenue', value: `$${totalRevenue.toFixed(2)}`, color: '#0070c9' },
      { label: 'Attributed Bookings', value: totalBookings, color: '#00d4aa' },
      { label: 'Total Campaigns', value: campaigns.length, color: '#f7931e' },
      { label: 'Total Delivered', value: totalDelivered.toLocaleString(), color: '#9b59b6' },
      { label: 'Open Rate', value: `${openRate}%`, color: '#e74c3c' },
      { label: 'Click Rate', value: `${clickRate}%`, color: '#ff6b35' }
    ];

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
        {kpis.map((kpi, i) => (
          <div key={i} className="analytics-chart-card" style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: kpi.color, marginBottom: '4px' }}>{kpi.value}</div>
            <div style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>{kpi.label}</div>
          </div>
        ))}
      </div>
    );
  };

  // Campaign Performance: Revenue by channel bar chart
  const CampaignRevenueByChannelChart = ({ campaigns }) => {
    const channelColors = { email: '#0070c9', push: '#f7931e', whatsapp: '#00d4aa' };
    const channelLabels = { email: 'Email', push: 'Push', whatsapp: 'WhatsApp' };

    // Aggregate revenue by channel
    const byChannel = {};
    campaigns.forEach(c => {
      if (!byChannel[c.channel]) byChannel[c.channel] = { revenue: 0, bookings: 0, count: 0 };
      byChannel[c.channel].revenue += c.attributedRevenue;
      byChannel[c.channel].bookings += c.attributedBookings;
      byChannel[c.channel].count += 1;
    });
    const channelData = Object.entries(byChannel).map(([ch, vals]) => ({ channel: ch, ...vals }));
    const maxRevenue = Math.max(...channelData.map(d => d.revenue), 1);

    return (
      <div className="analytics-chart-card line-chart-full-width">
        <div className="chart-header">
          <h3>Attributed Revenue by Channel</h3>
        </div>
        <div className="analytics-chart-content">
          {channelData.map((item, i) => {
            const pct = (item.revenue / maxRevenue) * 100;
            return (
              <div key={i} className="analytics-chart-item">
                <div className="chart-item-info">
                  <div className="chart-label">{channelLabels[item.channel] || item.channel}</div>
                  <div className="chart-subtitle">{item.count} campaigns · {item.bookings} bookings</div>
                </div>
                <div className="chart-value">${item.revenue.toFixed(2)}</div>
                <div className="chart-bar-container">
                  <div className="analytics-chart-bar" style={{ width: `${pct}%`, backgroundColor: channelColors[item.channel] || '#0070c9' }} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="chart-summary">
          <div className="summary-item">
            <span className="summary-label">Total Attributed Revenue:</span>
            <span className="summary-value">${campaigns.reduce((s, c) => s + c.attributedRevenue, 0).toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Campaign Performance: Detail table
  const CampaignPerfTable = ({ campaigns }) => {
    const channelBadgeColors = { email: '#0070c9', push: '#f7931e', whatsapp: '#00d4aa' };
    return (
      <div className="analytics-chart-card line-chart-full-width">
        <div className="chart-header">
          <h3>Campaign Breakdown</h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                {['Campaign', 'Channel', 'Sent', 'Recipients', 'Delivered', 'Opens', 'Clicks', 'Attributed Rev.', 'Bookings'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Attributed Rev.' || h === 'Bookings' ? 'right' : 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c, i) => (
                <tr key={c.id + i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '10px 12px', fontWeight: '500', color: '#333', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.title}>{c.title}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: channelBadgeColors[c.channel] || '#999', color: '#fff', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>
                      {c.channelLabel}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#666', whiteSpace: 'nowrap' }}>{c.sentAt.toLocaleDateString()}</td>
                  <td style={{ padding: '10px 12px', color: '#666', textAlign: 'right' }}>{c.recipientCount.toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', color: '#666', textAlign: 'right' }}>{(c.delivered || 0).toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', color: '#666', textAlign: 'right' }}>{c.opens.toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', color: '#666', textAlign: 'right' }}>{c.clicks.toLocaleString()}</td>
                  <td style={{ padding: '10px 12px', fontWeight: '600', color: '#0070c9', textAlign: 'right' }}>${c.attributedRevenue.toFixed(2)}</td>
                  <td style={{ padding: '10px 12px', color: '#333', textAlign: 'right' }}>{c.attributedBookings}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-section">
      <div className="reports-header">
        <h1>Analytics Dashboard</h1>
        <p>Real-time insights and performance analytics for your salon</p>
      </div>

      {analyticsLoading ? (
        <div className="loading-message">Loading analytics data...</div>
      ) : (
        <div className="analytics-dashboard">
          {analyticsData.revenueByStaffs.length === 0 && analyticsData.revenueByServices.length === 0 && analyticsData.revenueByPeriod.length === 0 ? (
            <div className="dashboard-notice">
              <h3>No Analytics Data Available</h3>
              <p>No completed transaction data found for your salon. The analytics will appear once you have customer data with completed transactions.</p>
              <button 
                onClick={loadAnalyticsData}
                className="generate-data-btn"
              >
                Refresh Analytics
              </button>
            </div>
          ) : (
            <>
              <div className="analytics-overview">
                <h3>Salon Performance Analytics</h3>
                <p>Real-time insights from your salon transaction data</p>
              </div>

              {/* Revenue by Period Chart - Full Width */}
              <div className="revenue-period-section">
                <LineChart
                  data={analyticsData.revenueByPeriod}
                  title="Revenue by Period"
                  filter={{
                    value: analyticsFilters.revenueByPeriod.period,
                    options: ['By Week', 'By Month']
                  }}
                  onFilterChange={(newPeriod) => {
                    setAnalyticsFilters(prev => ({
                      ...prev,
                      revenueByPeriod: { period: newPeriod }
                    }));
                  }}
                />
              </div>

              <div className="analytics-controls" style={{ marginTop: '24px', marginBottom: '24px' }}>
                <div className="analysis-period-control">
                  <label htmlFor="analysisPeriod">Analysis Period:</label>
                  <select
                    id="analysisPeriod"
                    value={analysisPeriod}
                    onChange={(e) => setAnalysisPeriod(e.target.value)}
                    className="filter-select analysis-period-select"
                  >
                    {analysisPeriodOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  <span className="analysis-period-note">
                    Applies to Revenue by Staff, Revenue by Service, and Loyalty Tier charts
                  </span>
                </div>
              </div>
              
              {/* Other Charts Grid */}
              <div className="analytics-grid">
                {/* Revenue Charts */}
                <AnalyticsChart
                  data={analyticsData.revenueByStaffs}
                  title="Revenue by Staff"
                  type="revenue-by-staffs"
                  filter={{
                    key: 'revenueByStaffs',
                    label: 'Service Types',
                    value: analyticsFilters.revenueByStaffs.serviceType,
                    options: analyticsData.services
                  }}
                  onFilterChange={(key, value) =>
                    setAnalyticsFilters(prev => ({
                      ...prev,
                      [key]: { ...prev[key], serviceType: value }
                    }))
                  }
                />

                <AnalyticsChart
                  data={analyticsData.revenueByServices}
                  title="Revenue by Service Type"
                  type="revenue-by-services"
                  filter={{
                    key: 'revenueByServices',
                    label: 'Staffs',
                    value: analyticsFilters.revenueByServices.staff,
                    options: analyticsData.staffs
                  }}
                  onFilterChange={(key, value) =>
                    setAnalyticsFilters(prev => ({
                      ...prev,
                      [key]: { ...prev[key], staff: value }
                    }))
                  }
                />

                <AnalyticsChart
                  data={analyticsData.tierSpending}
                  title="Total Spend by Loyalty Tier"
                  type="tier-spending"
                  filter={{
                    key: 'tierSpending',
                    label: 'Tiers',
                    value: analyticsFilters.tierSpending.tier,
                    options: ['Bronze', 'Silver', 'Gold', 'Platinum']
                  }}
                  onFilterChange={(key, value) =>
                    setAnalyticsFilters(prev => ({
                      ...prev,
                      [key]: { ...prev[key], tier: value }
                    }))
                  }
                />

                {/* Visits Charts */}
                <AnalyticsChart
                  data={analyticsData.visitsByStaff}
                  title="Total Visits by Staff"
                  type="visits-by-staff"
                  filter={{
                    key: 'visitsByStaff',
                    label: 'Service Types',
                    value: analyticsFilters.visitsByStaff.serviceType,
                    options: analyticsData.services
                  }}
                  onFilterChange={(key, value) =>
                    setAnalyticsFilters(prev => ({
                      ...prev,
                      [key]: { ...prev[key], serviceType: value }
                    }))
                  }
                  formatValue={(value) => `${value} visits`}
                />

                <AnalyticsChart
                  data={analyticsData.visitsByService}
                  title="Total Visits by Service Type"
                  type="visits-by-service"
                  filter={{
                    key: 'visitsByService',
                    label: 'Staffs',
                    value: analyticsFilters.visitsByService.staff,
                    options: analyticsData.staffs
                  }}
                  onFilterChange={(key, value) =>
                    setAnalyticsFilters(prev => ({
                      ...prev,
                      [key]: { ...prev[key], staff: value }
                    }))
                  }
                  formatValue={(value) => `${value} visits`}
                />

                <AnalyticsChart
                  data={analyticsData.tierVisits}
                  title="Total Visits by Loyalty Tier"
                  type="tier-visits"
                  filter={{
                    key: 'tierVisits',
                    label: 'Tiers',
                    value: analyticsFilters.tierVisits.tier,
                    options: ['Bronze', 'Silver', 'Gold', 'Platinum']
                  }}
                  onFilterChange={(key, value) =>
                    setAnalyticsFilters(prev => ({
                      ...prev,
                      [key]: { ...prev[key], tier: value }
                    }))
                  }
                  formatValue={(value) => `${value} visits`}
                />
              </div>

              {/* Campaign Performance Section */}
              <div className="campaign-performance-section" style={{ marginTop: '40px' }}>
                <div className="analytics-overview" style={{ marginBottom: '20px' }}>
                  <h3>Campaign Performance</h3>
                  <p>Revenue attributed to campaigns (30-day attribution window after send date)</p>
                </div>

                {/* Campaign Performance Filters */}
                <div className="analytics-controls" style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '14px', fontWeight: '500', color: '#333', whiteSpace: 'nowrap' }}>Channel:</label>
                      <select
                        value={campaignPerfFilters.channel}
                        onChange={e => setCampaignPerfFilters(prev => ({ ...prev, channel: e.target.value }))}
                        className="filter-select analysis-period-select"
                      >
                        <option value="all">All Channels</option>
                        <option value="email">Email</option>
                        <option value="push">Push</option>
                        <option value="whatsapp">WhatsApp</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ fontSize: '14px', fontWeight: '500', color: '#333', whiteSpace: 'nowrap' }}>Period:</label>
                      <select
                        value={campaignPerfFilters.period}
                        onChange={e => setCampaignPerfFilters(prev => ({ ...prev, period: e.target.value }))}
                        className="filter-select analysis-period-select"
                      >
                        {campaignPeriodOptions.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                {campaignPerfLoading ? (
                  <div className="loading-message">Loading campaign performance...</div>
                ) : campaignPerformance.length === 0 ? (
                  <div className="analytics-chart-card line-chart-full-width">
                    <div className="no-chart-data">No campaign data available for the selected period and channel.</div>
                  </div>
                ) : (
                  <>
                    {/* Summary KPI row */}
                    <CampaignPerfSummary campaigns={campaignPerformance} />

                    {/* Revenue by channel bar chart */}
                    <div className="revenue-period-section" style={{ marginTop: '24px' }}>
                      <CampaignRevenueByChannelChart campaigns={campaignPerformance} />
                    </div>

                    {/* Campaign table */}
                    <div style={{ marginTop: '24px' }}>
                      <CampaignPerfTable campaigns={campaignPerformance} />
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboardTab;